<template>
  <div v-if="isAuthenticated" :class="[
    'sideBar',
    isExpanded ? 'sideBar-expanded' : 'sideBar-collapsed',
    'header-sidebar-color',
    { 'mobile-sidebar': isMobile }
  ]">
    <div class="menu-toggle-wrap" v-if="!isMobile || isExpanded">
      <button class="menu-toggle" @click="ToggleMenu">
        <span class="space-icon space-icon-expand" />
      </button>
    </div>
    <div class="menu mt-1">
      <SideBarMenuItem 
        title="Overview" 
        icon="space-icon space-icon-home" 
        to="/" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        v-if="!isSubAffiliate"
        title="Advertiser Earnings" 
        icon="space-icon space-icon-dollar" 
        to="/advertiserEarnings" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        :title="siteEarningsTitle" 
        icon="pi-fw pi-sitemap" 
        to="/siteEarnings" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        v-if="isSuperAdmin || isFinance"
        title="Voonix Advertisers" 
        icon="pi-fw pi-warehouse" 
        to="/voonixAdvertisers" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        v-if="isAdmin"
        title="Advertisers" 
        icon="space-icon space-icon-advertisers" 
        to="/advertisers" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        title="Advertisers" 
        v-if="!isAdmin && !isSubAffiliate"
        :disabled="!isApproved"
        icon="space-icon space-icon-advertisers" 
        to="/advertisersV2" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        v-if="isSuperAdmin"
        title="Campaigns" 
        icon="pi-fw pi-ticket" 
        to="/campaigns" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        v-if="isAdmin || isSubAffiliate"
        title="Users" 
        icon="pi-fw pi-users" 
        to="/users" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        v-if="!isSubAffiliate"
        :title="$store.getters.isSeo ? 'Sites' : 'Toplists'" 
        icon="pi-fw pi-sitemap" 
        to="/sites" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
        <SideBarMenuItem 
          v-if="!isSubAffiliate"
          title="Postbacks" 
          icon="pi-server" 
          to="/postbacks?showing=postbacklog" 
          :isMobile="isMobile"
          :isExpanded="isExpanded"
        />
        <SideBarMenuItem
          v-if="isAdmin"
          title="Audit Log"
          icon="pi-fw pi-history"
          to="/auditLog"
          :isMobile="isMobile"
          :isExpanded="isExpanded"
        />
        <SideBarMenuItem
          v-if="isSuperAdmin"
          title="Tools"
          icon="pi-fw pi-wrench" 
          to="/tools" 
          :isMobile="isMobile"
          :isExpanded="isExpanded"
        />
        <SideBarMenuItem 
          v-if="isSuperAdmin"
          title="Super Admin" 
          icon="pi-fw pi-wrench" 
          to="/superadmin" 
          :isMobile="isMobile"
          :isExpanded="isExpanded"
        />
    </div>
    <div class="flex"></div>
    <div v-if="isAuthenticated" class="menu">
      <SideBarMenuItem 
        title="Finance" 
        icon="space-icon space-icon-wallet" 
        to="/finance" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
      <SideBarMenuItem 
        title="Settings" 
        icon="space-icon space-icon-settings" 
        to="/settings" 
        :isExpanded="isExpanded"
        :isMobile="isMobile"
        @menuClick="ToggleMenu"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import SideBarMenuItem from './SideBarMenuItem.vue';

const props = defineProps({
  isExpanded: {
    type: Boolean,
    required: true
  },
  isMobile: {
    type: Boolean,
    required: true
  }
});


// Remove local isExpanded ref since it's now a prop
const emit = defineEmits(['toggle']);

const store = useStore();

const isAuthenticated = computed(() => store.getters.isAuthenticated);
const isFinance = computed(() => store.getters.isFinance);
const isAdmin = computed(() => store.getters.isAdmin);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const isSubAffiliate = computed(() => store.getters.isSubAffiliate);
const isApproved = computed(() => store.getters.isApproved);
const previousWidth = ref(window.innerWidth);
const isImpersonated = computed(() => store.getters.isImpersonated);
// Add window resize handler
const handleResize = () => {
  const currentWidth = window.innerWidth;
  // Only collapse if we're actively resizing smaller
  if (currentWidth < previousWidth.value && currentWidth < 768) {
    emit('toggle', false);
  }
  previousWidth.value = currentWidth;
};

// Add event listener when component is mounted
onMounted(() => {
  window.addEventListener('resize', handleResize);

  // Fix for mobile viewport height
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);

  window.addEventListener('resize', () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  });
});

// Clean up event listener when component is unmounted
onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

const ToggleMenu = () => {
  emit('toggle', !props.isExpanded);
};

const siteEarningsTitle = computed(() => {
  if (store.getters.isAdmin) {
    return 'Publisher Earnings'
  }
  if (store.getters.isSubAffiliate) {
    return 'Publisher Earnings'
  }
  return store.getters.isSeo ? 'Site Earnings' : 'Toplist Earnings'
})

defineExpose({ ToggleMenu });
</script>

<style scoped>
#header-logo{
  max-width: 64%;
  height: auto;
}

.flex {
  flex: 1 1 auto;
}

.menu-toggle-wrap {
  display: flex;
  justify-content: flex-end;
  position: relative;
  top: 0;
  transition: 0.5s ease-in-out;
}

.menu-toggle {
  transition: 0.2s ease-in-out;
  font-size: 1.5rem;
}

.menu-toggle .pi-angle-double-right:hover {
  transform: translateX(0.5rem);
  transition: 0.5s ease-in-out;
}

.menu {
  margin: 0 -1rem;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.menu::-webkit-scrollbar {
  display: none;
}

h3,
.button .menu-text {
  opacity: 0;
  transform: 0.3s ease-out;
  ;
}

.menu .router-link-exact-active {
  background-color: var(--dark-alt);
  border-right: 5px solid var(--primary);
}

.menu .router-link-exact-active .pi,
.menu .router-link-exact-active .menu-text {
  color: var(--primary)
}

.menu .button:hover {
  transform: translateX(0.6rem);
  background-color: var(--dark-alt);
  color: hsl(222, 57%, 43%);
}

.menu .button .pi {
  font-size: 1.5rem;
  transition: 0.2s ease-out;
}

.menu .button .pi {
  transition: 0.2s ease-out;
}

.sideBar {
  color: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1rem;
  transition: 0.2s ease-in-out;
  position: sticky;
  top: 0;
  left: 0;
  -webkit-overflow-scrolling: touch;
}

.sideBar-expanded {
  width: var(--expanded-sidebar-width);
}


.sideBar-expanded .menu-toggle-wrap .menu-toggle {
  transform: rotate(-180deg);
}

.sideBar-expanded h3 {
  opacity: 1;
}

.sideBar-expanded .button .menu-text {
  opacity: 1;
}

.sideBar-expanded .button .pi {
  margin-right: 1rem;
}

.sideBar-collapsed {
  width: var(--collapsed-sidebar-width);
}

.logo {
  height: 4.5rem;
  width: 3.5rem;
}

/* Add media query for mobile */
@media (max-width: 768px) {
  .sideBar {
    min-height: -webkit-fill-available;
    z-index: 1000;
  }

  .menu {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    /* padding-bottom: 4rem; */
  }
}

.mobile-sidebar {
  position: fixed;
  width: 70% !important; /* Override default expanded width */
  height: 100svh;
  z-index: 1000;
  left: 0;
  top: 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease-in-out;
}

/* When collapsed on mobile, hide completely */
.sideBar-collapsed.mobile-sidebar {
  transform: translateX(-100%);
}

/* When expanded on mobile, show */
.sideBar-expanded.mobile-sidebar {
  transform: translateX(0);
}

</style>